import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16" role="navigation" aria-label="主导航">
        <Link
          to="/"
          className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
          aria-label="妖精漫画网首页"
        >
          <span className="text-2xl font-heading text-primary-500 font-bold tracking-wide">
            妖精漫画
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1" role="menubar">
          <Link
            to="/"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              location.pathname === '/'
                ? 'text-primary-500 bg-primary-50'
                : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50'
            }`}
            role="menuitem"
          >
            首页
          </Link>
          {categories.slice(0, 5).map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === `/category/${cat.slug}`
                  ? 'text-primary-500 bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50'
              }`}
              role="menuitem"
            >
              {cat.name}
            </Link>
          ))}
          <Link
            to="/category/all"
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50"
            role="menuitem"
          >
            更多
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="搜索漫画"
            >
              <i className="ri-search-line text-foreground-600 text-lg"></i>
            </button>
            {searchOpen && (
              <div className="absolute right-0 top-12 w-72 bg-background-50 rounded-xl border border-background-200 shadow-lg p-3">
                <div className="flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2">
                  <i className="ri-search-line text-foreground-400 text-sm"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索漫画..."
                    className="bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none flex-1"
                  />
                </div>
              </div>
            )}
          </div>
          <Link
            to="/about"
            className="px-5 py-2 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            关于我们
          </Link>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 cursor-pointer"
            aria-label="搜索"
          >
            <i className="ri-search-line text-foreground-600"></i>
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 cursor-pointer"
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-foreground-600`}></i>
          </button>
        </div>
      </nav>

      {searchOpen && (
        <div className="md:hidden px-4 pb-3">
          <div className="flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索漫画..."
              className="bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none flex-1"
            />
          </div>
        </div>
      )}

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 transition-all duration-200">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="px-4 py-3 text-sm font-medium rounded-lg transition-colors text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 cursor-pointer"
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-4 py-3 text-sm font-medium rounded-lg transition-colors text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="px-4 py-3 text-sm font-medium rounded-lg transition-colors text-primary-500 hover:bg-primary-50/50 cursor-pointer mt-1"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}